import React from "react";
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Link2,
  Loader2,
  ShieldCheck,
  Tag,
  Users,
  XCircle,
} from "lucide-react";
import type {
  LeveragedAuthorizationResponse,
  LeveragedAuthorizationStatus,
  SspPartyResponse,
} from "../../../../api/frameworksApi";
import {
  FormDatePicker,
  FormField,
  FormInput,
  FormSelect,
} from "../../../components/Modal";
import { toddmmyyyy } from "../constants";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { getPageWindow } from "../utils/pagination";

export interface LeveragedAuthorizationsTabProps {
  leveragedAuthsLoading: boolean;
  leveragedAuths: LeveragedAuthorizationResponse[];
  pagLevAuths: LeveragedAuthorizationResponse[];
  filtLevAuths: LeveragedAuthorizationResponse[];
  editingLevAuthId: string | null;
  setEditingLevAuthId: React.Dispatch<React.SetStateAction<string | null>>;
  editLevAuthForm: { title: string; partyUuid: string; dateAuthorized: string };
  setEditLevAuthForm: React.Dispatch<
    React.SetStateAction<{
      title: string;
      partyUuid: string;
      dateAuthorized: string;
    }>
  >;
  savingLevAuth: boolean;
  handleUpdateLevAuth: () => void;
  parties: SspPartyResponse[];
  openLevAuthMenuId: string | null;
  setOpenLevAuthMenuId: React.Dispatch<React.SetStateAction<string | null>>;
  updatingLevAuthStatusId: string | null;
  handleUpdateLevAuthStatus: (
    authId: string,
    status: LeveragedAuthorizationStatus,
  ) => void;
  tabPage: number;
  tabPageSize: number;
  setCurrentTabPagination: (pagination: {
    page: number;
    pageSize: number;
  }) => void;
}

export function LeveragedAuthorizationsTab({
  leveragedAuthsLoading,
  leveragedAuths,
  pagLevAuths,
  filtLevAuths,
  editingLevAuthId,
  setEditingLevAuthId,
  editLevAuthForm,
  setEditLevAuthForm,
  savingLevAuth,
  handleUpdateLevAuth,
  parties,
  openLevAuthMenuId,
  setOpenLevAuthMenuId,
  updatingLevAuthStatusId,
  handleUpdateLevAuthStatus,
  tabPage,
  tabPageSize,
  setCurrentTabPagination,
}: LeveragedAuthorizationsTabProps) {
  return (
    <>
      {leveragedAuthsLoading ? (
        <LoadingSpinner text="Loading leveraged authorizations" />
      ) : leveragedAuths.length > 0 ? (
        <>
          {leveragedAuths.some((a) => a.status === "revoked") && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg text-xs text-red-700 dark:text-red-400 font-medium">
              <XCircle className="size-4 shrink-0" />
              One or more provider authorizations have been revoked. Review inherited controls in the Control Implementations tab.
            </div>
          )}
          <div className="rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
            {pagLevAuths.map((auth) => (
              <div
                key={auth.id}
                className={`px-5 py-4 transition-colors ${auth.status === "revoked"
                    ? "bg-red-50/30 dark:bg-red-500/5"
                    : auth.status === "expired"
                      ? "bg-amber-50/30 dark:bg-amber-500/5"
                      : "hover:bg-muted/40"
                  }`}
              >
                {editingLevAuthId === auth.id ? (
                  /* ── Inline edit form ── */
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Title" required>
                        <FormInput
                          value={editLevAuthForm.title}
                          onChange={(v) =>
                            setEditLevAuthForm((f) => ({ ...f, title: v }))
                          }
                          placeholder="Authorization title"
                        />
                      </FormField>
                      <FormField label="Authorizing Party">
                        <FormSelect
                          options={[
                            { label: "— None —", value: "" },
                            ...parties.map((p) => ({
                              label: `${p.name} (${p.partyType})`,
                              value: p.partyUuid,
                            })),
                          ]}
                          placeholder="Select a party"
                          value={editLevAuthForm.partyUuid}
                          onChange={(v) =>
                            setEditLevAuthForm((f) => ({
                              ...f,
                              partyUuid: v,
                            }))
                          }
                        />
                      </FormField>
                    </div>
                    <FormField label="Date Authorized">
                      <FormDatePicker
                        value={editLevAuthForm.dateAuthorized}
                        onChange={(v) =>
                          setEditLevAuthForm((f) => ({
                            ...f,
                            dateAuthorized: v,
                          }))
                        }
                      />
                    </FormField>
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={() => setEditingLevAuthId(null)}
                        className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateLevAuth}
                        disabled={!editLevAuthForm.title || savingLevAuth}
                        className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                      >
                        {savingLevAuth ? "Saving" : "Save"}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Read-only display ── */
                  <div className="space-y-2">
                    {/* Revoked / expired banners */}
                    {auth.status === "revoked" && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400 text-xs font-medium">
                        <XCircle className="size-3.5 shrink-0" />
                        This leveraged authorization has been revoked. Inherited controls from this provider may no longer be valid.
                      </div>
                    )}
                    {auth.status === "expired" && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 text-xs font-medium">
                        <AlertTriangle className="size-3.5 shrink-0" />
                        This leveraged authorization has expired. Review with the provider to renew.
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center size-10 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5 ring-1 ring-indigo-100 dark:ring-indigo-500/20">
                        <Link2 className="size-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-foreground">{auth.title}</p>
                          {/* Status badge */}
                          {(!auth.status || auth.status === "active") && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20">
                              <CheckCircle2 className="size-2.5" /> Active
                            </span>
                          )}
                          {auth.status === "revoked" && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20">
                              <XCircle className="size-2.5" /> Revoked
                            </span>
                          )}
                          {auth.status === "expired" && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20">
                              <Clock className="size-2.5" /> Expired
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-x-4 gap-y-1.5 mt-2 flex-wrap text-xs">
                          {auth.partyUuid && (
                            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                              <Users className="size-3.5 text-sky-600 dark:text-sky-400" />
                              <span className="text-muted-foreground">Party:</span>
                              <span className="font-mono text-foreground">{auth.partyUuid.slice(0, 12)}…</span>
                            </span>
                          )}
                          {auth.dateAuthorized && (
                            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                              <Calendar className="size-3.5 text-indigo-600 dark:text-indigo-400" />
                              <span className="text-muted-foreground">Authorized:</span>
                              <span className="text-foreground font-medium">{toddmmyyyy(auth.dateAuthorized)}</span>
                            </span>
                          )}
                          {auth.sourceSspVersion && (
                            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                              <Tag className="size-3.5 text-violet-600 dark:text-violet-400" />
                              <span className="text-muted-foreground">Version:</span>
                              <span className="text-foreground font-medium">{auth.sourceSspVersion}</span>
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                            <ShieldCheck className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                            <span className="text-muted-foreground">Scope:</span>
                            <span className="text-foreground font-medium">
                              {auth.controlScope ? auth.controlScope.join(", ") : "All controls"}
                            </span>
                          </span>
                        </div>
                      </div>
                      {/* Update Status dropdown (non-terminal only) */}
                      {auth.status !== "revoked" && auth.status !== "expired" && (
                        <div className="relative shrink-0">
                          <button
                            onClick={() => setOpenLevAuthMenuId(openLevAuthMenuId === auth.id ? null : auth.id)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-border rounded-lg hover:bg-muted transition-colors"
                          >
                            Update Status <ChevronDown className="size-3" />
                          </button>
                          {openLevAuthMenuId === auth.id && (
                            <div className="absolute right-0 top-full mt-1 z-10 bg-popover border border-border rounded-lg shadow-lg min-w-[160px] overflow-hidden">
                              <button
                                onClick={() => { setOpenLevAuthMenuId(null); handleUpdateLevAuthStatus(auth.id, "revoked"); }}
                                disabled={updatingLevAuthStatusId === auth.id}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                              >
                                <XCircle className="size-3.5" /> Mark as Revoked
                              </button>
                              <button
                                onClick={() => { setOpenLevAuthMenuId(null); handleUpdateLevAuthStatus(auth.id, "expired"); }}
                                disabled={updatingLevAuthStatusId === auth.id}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors"
                              >
                                <Clock className="size-3.5" /> Mark as Expired
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {updatingLevAuthStatusId === auth.id && (
                        <Loader2 className="size-4 animate-spin text-muted-foreground shrink-0" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {filtLevAuths.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
              <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                Showing{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {Math.min((tabPage - 1) * tabPageSize + 1, filtLevAuths.length)}–{Math.min(tabPage * tabPageSize, filtLevAuths.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {filtLevAuths.length}
                </span>
              </p>
              {Math.ceil(filtLevAuths.length / tabPageSize) > 1 && (
                <div className="flex items-center gap-0.5">
                  <button
                    disabled={tabPage === 1}
                    onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="size-3" />
                  </button>

                  {getPageWindow(tabPage, Math.ceil(filtLevAuths.length / tabPageSize)).map((p, idx) =>
                    p === "dots" ? (
                      <span
                        key={`dots-${idx}`}
                        className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                        className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                            ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                            : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                          }`}
                      >
                        {p}
                      </button>
                    ),
                  )}

                  <button
                    disabled={tabPage >= Math.ceil(filtLevAuths.length / tabPageSize)}
                    onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtLevAuths.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="size-3" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
          <ExternalLink className="size-8 text-muted-foreground mb-3 mx-auto" />
          <p className="text-sm font-medium">No leveraged authorizations</p>
          <p className="text-xs text-muted-foreground mt-1">
            Reference authorizations from other systems that this SSP
            inherits controls from
          </p>
        </div>
      )}
    </>
  );
}
